"use client";

/**
 * @file ソート可能なサイドバーグループの統合基底コンポーネント
 * プロジェクトとダッシュボード両方に対応するジェネリックな実装
 */

import { type ReactNode, useMemo } from "react";
import { SidebarMenu } from "@/components/ui/sidebar";
import { SortableItem } from "@/components/ui/sortable-item";
import { cn } from "@/lib/utils";
import { useRefreshWorkspaceSidebar } from "../../../_contexts/workspace-sidebar-data-context";
import { updateDashboardOrder, updateProjectOrder } from "../../../action";
import { useSidebarSearchGroupProjects } from "./sidebar-search-context";
import { SortableContainer } from "./sortable-container";
import { addActiveStateToProjects } from "./sortable-utils";
import type { BaseProject, ProjectGroupType, SidebarItemType } from "./types";
import { PROJECT_TYPE_TO_SIDEBAR_TYPE } from "./types";
import { useSortableItems } from "./use-sortable-items";

interface SortableSidebarGroupsBaseProps<T extends BaseProject> {
  workspaceId: string;
  items: T[];
  projectType: ProjectGroupType;
  currentPathname: string;
  renderItem: (
    item: T & { isProjectActive: boolean },
    isDragging: boolean,
    isUpdating: boolean,
  ) => ReactNode;
  renderDragOverlayItem?: (
    item: T & { isProjectActive: boolean },
    isDragging: boolean,
    isUpdating: boolean,
  ) => ReactNode;
  className?: string;
  disableSorting?: boolean;
}

/**
 * ソート可能なサイドバーグループの統合基底コンポーネント
 * プロジェクトとダッシュボードの両方に対応
 */
export function SortableSidebarGroupsBase<T extends BaseProject>({
  workspaceId,
  items,
  projectType,
  currentPathname,
  renderItem,
  renderDragOverlayItem,
  className,
  disableSorting = false,
}: SortableSidebarGroupsBaseProps<T>) {
  const { isFiltering, projects: projectMatches } =
    useSidebarSearchGroupProjects(projectType);
  const refreshWorkspaceSidebar = useRefreshWorkspaceSidebar();
  // プロジェクトタイプをサイドバーアイテムタイプに変換
  const sidebarItemType: SidebarItemType =
    PROJECT_TYPE_TO_SIDEBAR_TYPE[projectType];

  // アイテムにアクティブ状態を追加
  const itemsWithActiveState = useMemo(() => {
    return addActiveStateToProjects(
      items,
      currentPathname,
      workspaceId,
      projectType,
    );
  }, [items, currentPathname, workspaceId, projectType]);

  // 共通フックを使用してDnDロジックを管理
  const {
    orderedItems,
    isUpdating,
    activeId,
    sensors,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    dndContextId,
  } = useSortableItems({
    workspaceId,
    items: itemsWithActiveState,
    sidebarItemType,
    updateAction: async (ids, type) => {
      // タイプに応じて適切なアクションを呼び分け
      if (type === "DASHBOARD") {
        const result = await updateDashboardOrder(workspaceId, ids);
        if (!result.error) {
          refreshWorkspaceSidebar("order-mutation");
        }
        return { error: result.error ? new Error(result.error) : undefined };
      }

      const result = await updateProjectOrder(
        workspaceId,
        ids,
        type as "INDEX_WORKER" | "SEARCH_ANALYTICS" | "USER_ANALYTICS",
      );
      if (!result.error) {
        refreshWorkspaceSidebar("order-mutation");
      }
      return { error: result.error ? new Error(result.error) : undefined };
    },
  });

  const searchVisibleProjectIds = useMemo(
    () => new Set(projectMatches.map((project) => project.id)),
    [projectMatches],
  );

  // アイテムのレンダリング関数
  const renderSortableItem = (item: T & { isProjectActive: boolean }) => {
    const isActiveItem = activeId === item.id;
    // activeIdが存在する = 全体がドラッグ中
    const isGlobalDragging = activeId !== null;

    // 検索中も item を unmount せず、検索前の project 開閉状態を維持する。
    return (
      <SortableItem
        key={item.id}
        id={item.id}
        className={cn(
          "mb-1", // プロジェクト間のスペーシング
          isFiltering && !searchVisibleProjectIds.has(item.id) && "hidden",
          isUpdating && "pointer-events-none",
          isActiveItem && "invisible", // ドラッグ中の元要素を非表示（スペースは維持）
        )}
        disabled={disableSorting || isFiltering}
        showDropIndicator={!disableSorting && !isFiltering}
        cursorVariant="default"
      >
        {renderItem(item, isGlobalDragging, isUpdating)}
      </SortableItem>
    );
  };

  // DragOverlay用のレンダリング関数（開閉状態を維持）
  const renderDragOverlay = (item: T) => {
    // 重要: DragOverlay内ではSortableItemを使わず、直接renderItemを呼び出す
    // これは公式ドキュメントの推奨パターンに従っている（useSortableの重複を避ける）
    // renderDragOverlayItemが提供されている場合はそれを使用、そうでない場合はrenderItemを使用
    const renderFunction = renderDragOverlayItem || renderItem;
    return renderFunction(
      item as T & { isProjectActive: boolean },
      true,
      false,
    );
  };

  if (orderedItems.length === 0) {
    return null;
  }

  return (
    <SidebarMenu className={className}>
      <SortableContainer
        items={orderedItems}
        renderItem={renderSortableItem}
        renderDragOverlay={renderDragOverlay}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        activeId={activeId}
        isUpdating={isUpdating}
        sensors={sensors}
        dndContextId={dndContextId}
      />
    </SidebarMenu>
  );
}
